<template>
  <transition name="fade" mode="out-in">
    <AgGridVue
      :key="isDark ? 'dark' : 'light'"
      style="flex: 1;"
      :grid-options="options"
      :rowData="rowData"
      :overlayNoRowsTemplate="noRowsTemplate"
      @grid-ready="onGridReady"
      @selection-changed="onSelectionChanged"
      @first-data-rendered="onFirstDataRendered"
    />
  </transition>
</template>

<script setup lang="ts">
import { useDark } from '@vueuse/core';
import { AgGridVue } from 'ag-grid-vue3'
import { GridApi, GridOptions } from 'ag-grid-community';
import { themeQuartz, colorSchemeDark } from 'ag-grid-community';

import TrackingActions from './cells/TrackingActions.vue';
import { useParticipations } from '@/queries/participant.query.js';
import { Participant } from '@/types/participant.js';
import { ResultRow } from '@/types/results.js';
import CompatibilityCell from './cells/CompatibilityCell.vue';
import ResultsActions from './cells/ResultsActions.vue';

const { locale, t } = useI18n();
const isDark = useDark();
const appStore = useAppStore();
const { data: participations, isPending } = useParticipations();

const noRowsTemplate = ref(`<div>${t('app.tables.resultsTable.noRowTemplate')}</div>`);

let gridApi: GridApi;

const props = withDefaults(defineProps<{
  search: {
    value: string,
    searchable: any[],
  },
  filters: any,
}>(), {
  search: () => {
    return {
      value: "",
      searchable: [
        "name",
      ],
    }
  }
});

const options = computed(() => {
  return {
    theme: isDark.value ? themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#1F1F1F",
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
      spacing: 9,
    }).withPart(colorSchemeDark) : themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#FAFAFA",
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
      spacing: 9,
    }),
    scrollbarWidth: 12,
    suppressCellFocus: true,
    rowSelection: {
      mode: 'multiRow',
      enableClickSelection: true,
      enableSelectionWithoutKeys: true,
    },
    defaultColDef: {
      sortable: true,
      flex: 1,
      resizable: false,
      unSortIcon: true
    },
    columnDefs: [
      {
        headerName: t('other.table.columns.compatibility'),
        field: 'compatibility',
        sortable: true,
        filter: false,
        flex: 0,
        headerClass: 'header-center',
        cellRenderer: CompatibilityCell,
        valueGetter: params => params.data.results.compatibility,
      },
      { field: 'talent', headerName: t('other.table.columns.participant'), flex: 1 },
      { field: 'email', headerName: t('other.table.columns.email'), flex: 1 },
      { field: 'date', headerName: t('other.table.columns.evalDate'), flex: 0,
        valueFormatter: (params) => {
          if (!params.value) return '';
          return new Intl.DateTimeFormat(locale.value, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }).format(new Date(params.value));
        },
      },
      {
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        filter: false,
        flex: 0,
        headerClass: 'header-right',
        cellRenderer: ResultsActions,
        cellRendererParams: {
          onView: (row: ResultRow) => onView(row),
        }
      },
    ]
  } as GridOptions<ResultRow>;
});

const filteredParticipants = computed(() => {
  if (isPending.value) return [];
  return participations.value.filter((participant) => {
    if (!participant.results) return false;
    if (!props.filters) return true;

    // Filter by date
    const creationDate = new Date((participant.creation_time as string).replace(/Z$/, ""));
    const resultsCreationDate = new Date((participant.results.completed_at as string).replace(/Z$/, ""));
    if (props?.filters?.invitationDateFrom && creationDate.getTime() < props?.filters?.invitationDateFrom.getTime()) return false;
    if (props?.filters?.invitationDateTo && creationDate.getTime() > props?.filters?.invitationDateTo.getTime()) return false;

    if (props?.filters?.evaluationEndDateFrom && resultsCreationDate.getTime() < props?.filters?.evaluationEndDateFrom.getTime()) return false;
    if (props?.filters?.evaluationEndDateTo &&  resultsCreationDate.getTime() > props?.filters?.evaluationEndDateTo.getTime()) return false;

    // Filter by compatibility
    if ((participant?.results?.compatibility * 100) < props.filters.compatibilityScore[0] || (participant?.results?.compatibility * 100) > props.filters.compatibilityScore[1]) return false;
    
    // Filter by score
    const participantScoreEntries = Object.entries(participant?.results.scores).filter((v) => {
      if (v[1] <= -1) return false;
      if (v[1] === null) return false;
      return true;
    });
    if (props.filters.skills) {
      for (const scoreEntry of participantScoreEntries) {
        const scoreId = scoreEntry[0];
        const scoreValue = scoreEntry[1];
        const scoreMinMax = props.filters.skills[scoreId];
        if (!!scoreMinMax && ((scoreValue * 100) < scoreMinMax[0] || (scoreValue * 100) > scoreMinMax[1])) return false;
      }
    }

    // Filter by styles
    if (props.filters.selectedStyles) {
      const styles = getParticipantStyles(participant);
      for (const style of styles) {
        if (props.filters.selectedStyles[style.skill].length) {
          const participantHasFilteredStyle = props.filters.selectedStyles[style.skill].some((wording) => wording === style.style);
          if (!participantHasFilteredStyle) return false;
        }
      }
    }

    // Filter by badges
    if (props.filters.selectedBadges) {
      const participantBadges = Object.entries(participant?.results.badges).filter((entry) => {
        if (entry[1] === false) return false;
        return true;
      }).reduce((acc, entry) => {
        acc.push(entry[0]);
        return acc;
      }, []);
      const selectedBadgeEntries = Object.entries(props.filters.selectedBadges).filter((entry) => entry[1]);

      if (selectedBadgeEntries.length) {
        const some = selectedBadgeEntries.some((selectedBadgeEntry) => {
          const participantHasThisBadge = participantBadges.some((badge) => {
            if (selectedBadgeEntry[0] === badge) return true;
            return false;
          });
          return participantHasThisBadge;
        });
        if (!some) return false;
      }
    }
    return true;
  }).sort((pA, pB) => pB.results.compatibility - pA.results.compatibility);
});

const rowData = computed(() => {
  return filteredParticipants.value.map((p) => {
    return {
      id: p.participant_id,
      talent: `${p?.candidate?.first_name || p?.pending_first_name || ""} ${p?.candidate?.last_name || p?.pending_first_name || ""}`,
      email: p?.candidate?.email || p?.pending_email || "",
      date: p?.creation_time,
      results: p?.results,
    } as ResultRow;
  })
});

function getParticipantStyles(participant: Participant) {
  if (!participant?.results) return [];
  const styleEntries = Object.entries(participant?.results.styles);

  return styleEntries.reduce((acc, entry) => {
    const styleName = entry[0];
    acc.push({
      skill: styleName,
      style: entry[1] as string,
    });
    return acc;
  }, []);
}

function onView(row: ResultRow) {
  const rowParticipant = filteredParticipants.value.find((p) => p.participant_id === row.id);
  const selectedRows: ResultRow[] = gridApi.getSelectedRows();
  let selectedParticipants = filteredParticipants.value.filter((p) => selectedRows.some((r) => r.id === p.participant_id));
  if (!selectedParticipants.find((p) => p.participant_id === row.id) && rowParticipant) {
    selectedParticipants.push(rowParticipant);
  }
  appStore.openedCandidateResults = [...selectedParticipants];
  appStore.talentResultPanelIsOpen = true;
}

function onGridReady(params) {
  gridApi = params.api
}

function onSelectionChanged() {
  // const selectedRows = gridApi.getSelectedRows();
  // console.log('Selected:', selectedRows);
}

function onFirstDataRendered() {
  gridApi.autoSizeColumns(['compatibility']);
  gridApi.autoSizeColumns(['date']);
  gridApi.autoSizeColumns(['actions']);
}
</script>

<style lang="scss">
@import "@/assets/style/variables.scss";
</style>